import { useState, useEffect, useRef } from "react";
import { Mail, Loader2, Eye, EyeOff, X, ChevronLeft, ChevronRight, BarChart3, Link2, Copy, Check, CheckSquare, Square } from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { fetchNewsletterArchive, fetchArchivedNewsletter, fetchNewsletterViewStats, hideArchivedNewsletter, fmtDate } from "../services/api";
import { Button } from "../components/ui";
import { useAuth } from "../hooks/useAuth";

export default function NewsletterArchive() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [viewing, setViewing] = useState(null); // { id, title, bodyHtml }
  const [viewLoading, setViewLoading] = useState(false);
  const [viewStats, setViewStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState(new Set());
  const [bulkHiding, setBulkHiding] = useState(false);
  const iframeRef = useRef(null);
  const PAGE_SIZE = 25;

  const loadPage = (p) => {
    setLoading(true);
    fetchNewsletterArchive({ limit: PAGE_SIZE, offset: p * PAGE_SIZE, includeHidden: showHidden })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPage(page);
    setSelectedArchive(new Set());
  }, [page, showHidden]);

  const toggleArchive = (id) => setSelectedArchive((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAllArchive = () => {
    const eligible = items.filter((i) => !i.hidden).map((i) => i.id);
    const allSelected = eligible.length > 0 && eligible.every((id) => selectedArchive.has(id));
    setSelectedArchive(allSelected ? new Set() : new Set(eligible));
  };

  const handleBulkHideArchive = async () => {
    if (!window.confirm(`Hide ${selectedArchive.size} archived newsletter(s)?`)) return;
    setBulkHiding(true);
    try {
      await Promise.all([...selectedArchive].map((id) => hideArchivedNewsletter(id)));
      setSelectedArchive(new Set());
      loadPage(page);
    } catch (err) {
      alert(`Bulk hide failed: ${err.message}`);
    } finally {
      setBulkHiding(false);
    }
  };

  const handleView = async (item) => {
    setViewLoading(true);
    setViewStats(null);
    setCopied(false);
    setViewing({ id: item.id, title: item.title, date: item.date });
    try {
      const [full, stats] = await Promise.all([
        fetchArchivedNewsletter(item.id),
        fetchNewsletterViewStats(item.id).catch(() => null),
      ]);
      setViewing({ id: full.id, title: full.title, date: full.date, bodyHtml: full.bodyHtml, publicToken: full.publicToken });
      if (stats) setViewStats(stats);
    } catch {
      setViewing(null);
      alert("Failed to load newsletter");
    } finally {
      setViewLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!viewing?.publicToken) return;
    const baseUrl = window.location.origin;
    navigator.clipboard.writeText(`${baseUrl}/public/newsletter/${viewing.publicToken}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Write HTML into iframe once loaded
  useEffect(() => {
    if (viewing?.bodyHtml && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(viewing.bodyHtml);
      doc.close();
    }
  }, [viewing?.bodyHtml]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ padding: "28px 32px", maxWidth: 900 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <Mail size={22} color={colors.accent} />
        <h1 style={{
          fontFamily: fonts.heading, fontSize: 22, fontWeight: 700,
          color: colors.text, margin: 0,
        }}>
          Newsletter Archive
        </h1>
        <span style={{
          fontSize: 12, color: colors.textSecondary, marginLeft: "auto",
        }}>
          {total} newsletter{total !== 1 ? "s" : ""}
        </span>
        {isAdmin && (
          <Button
            variant={showHidden ? "primary" : "ghost"}
            size="sm"
            icon={showHidden ? Eye : EyeOff}
            onClick={() => setShowHidden((v) => !v)}
          >
            {showHidden ? "Showing Hidden" : "Show Hidden"}
          </Button>
        )}
      </div>

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: colors.textSecondary, padding: 40 }}>
          <Loader2 size={16} className="spin" />
          Loading archive...
        </div>
      )}

      {error && (
        <div style={{ color: "#ef4444", fontSize: 13, padding: 20 }}>{error}</div>
      )}

      {!loading && !error && (
        <>
          <div style={{
            border: `1px solid ${colors.border}`, borderRadius: 8,
            overflow: "hidden", background: colors.bgCard,
          }}>
            {isAdmin && items.filter((i) => !i.hidden).length > 0 && (
              <div
                onClick={(e) => { e.stopPropagation(); toggleAllArchive(); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 16px", borderBottom: `1px solid ${colors.border}`,
                  cursor: "pointer", fontSize: 11, color: colors.textSecondary,
                }}
              >
                {items.filter((i) => !i.hidden).length > 0 && items.filter((i) => !i.hidden).every((i) => selectedArchive.has(i.id))
                  ? <CheckSquare size={15} color={colors.accent} />
                  : <Square size={15} />}
                Select all
              </div>
            )}
            {items.map((item, i) => (
              <div
                key={item.id}
                onClick={() => handleView(item)}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "12px 16px", cursor: "pointer",
                  borderBottom: i < items.length - 1 ? `1px solid ${colors.border}` : "none",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                {isAdmin && !item.hidden && (
                  <div
                    onClick={(e) => { e.stopPropagation(); toggleArchive(item.id); }}
                    style={{ cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center" }}
                  >
                    {selectedArchive.has(item.id)
                      ? <CheckSquare size={16} color={colors.accent} />
                      : <Square size={16} color={colors.textSecondary} />}
                  </div>
                )}
                <div style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: `${colors.accent}15`, display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Mail size={15} color={colors.accent} />
                </div>
                <div style={{ flex: 1, minWidth: 0, opacity: item.hidden ? 0.5 : 1 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: colors.text,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    {item.title}
                    {item.hidden && <EyeOff size={11} color={colors.textSecondary} />}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                    {fmtDate(item.date)}
                  </div>
                </div>
                <Eye size={14} color={colors.textSecondary} style={{ flexShrink: 0 }} />
                {isAdmin && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm("Archive this newsletter? It will be hidden from all users except you.")) return;
                      try {
                        await hideArchivedNewsletter(item.id);
                        loadPage(page);
                      } catch (err) {
                        alert(`Archive failed: ${err.message}`);
                      }
                    }}
                    title="Archive (hide)"
                    style={{
                      background: "none", border: "none",
                      color: colors.danger, cursor: "pointer",
                      padding: 4, flexShrink: 0,
                      display: "flex", alignItems: "center",
                    }}
                  >
                    <EyeOff size={14} />
                  </button>
                )}
              </div>
            ))}
            {items.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: colors.textSecondary, fontSize: 13 }}>
                No archived newsletters found.
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 12, marginTop: 16,
            }}>
              <Button
                variant="ghost" size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft size={14} /> Prev
              </Button>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="ghost" size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Bulk hide floating bar */}
      {isAdmin && selectedArchive.size > 0 && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: colors.bgCard, border: `2px solid ${colors.accent}`, borderRadius: 12,
          padding: "12px 24px", display: "flex", alignItems: "center", gap: 16,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", zIndex: 9998,
        }}>
          <span style={{ fontSize: 13, color: colors.text, fontWeight: 600 }}>
            {selectedArchive.size} selected
          </span>
          <Button variant="danger" size="sm" icon={bulkHiding ? Loader2 : EyeOff} onClick={handleBulkHideArchive} disabled={bulkHiding}>
            Hide {selectedArchive.size} Newsletter{selectedArchive.size > 1 ? "s" : ""}
          </Button>
          <button
            onClick={() => setSelectedArchive(new Set())}
            style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary, padding: 4, display: "flex", alignItems: "center" }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Newsletter viewer modal */}
      {viewing && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.7)", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: 10,
            width: "min(90vw, 720px)", height: "90vh",
            display: "flex", flexDirection: "column",
            overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}>
            {/* Header */}
            <div style={{
              padding: "12px 16px", borderBottom: "1px solid #e5e7eb",
              display: "flex", alignItems: "center", gap: 10,
              background: "#f9fafb",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600, color: "#111",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {viewing.title}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>
                    {fmtDate(viewing.date)}
                  </span>
                  {viewStats && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      fontSize: 11, color: colors.accent, fontWeight: 500,
                    }}>
                      <BarChart3 size={11} />
                      {viewStats.uniqueViewers} viewer{viewStats.uniqueViewers !== 1 ? "s" : ""}
                      {viewStats.totalViews > viewStats.uniqueViewers && (
                        <span style={{ color: "#888", fontWeight: 400 }}>
                          ({viewStats.totalViews} views)
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              {viewing.publicToken && (
                <button
                  onClick={handleCopyLink}
                  title="Copy public link"
                  style={{
                    background: "none", border: `1px solid ${colors.border}`,
                    borderRadius: 6, cursor: "pointer", padding: "4px 8px",
                    display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 11, color: copied ? colors.success : colors.textSecondary,
                    transition: "color 0.2s",
                  }}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy link"}
                </button>
              )}
              <button
                onClick={() => setViewing(null)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: 4, color: "#666",
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            {viewLoading ? (
              <div style={{
                flex: 1, display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, color: "#888",
              }}>
                <Loader2 size={18} className="spin" />
                Loading...
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                title={viewing.title}
                style={{ flex: 1, border: "none", width: "100%" }}
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </div>
      )}    </div>
  );
}
